import type { ReactNode, RefObject } from 'react';
import { useEffect, useState } from 'react';
import { useTracking } from 'react-tracking';
import { useIsMounted } from '@simplywallst/ui-core';

import CrossSolid from '@simplywallst/ui-core/icons/CrossSolid';
import SetFairValueSolid from '@simplywallst/ui-core/icons/SetFairValueSolid';
import { Tooltip } from './Tooltip';

type Props = {
  children: ReactNode;
  show: boolean;
  onClick: () => void;
  containerRef: RefObject<HTMLDivElement>;
};

export const FairValueTooltip = ({
  children,
  show,
  onClick,
  containerRef,
}: Props) => {
  const [showTooltip, setShowTooltip] = useState(true);
  const { trackEvent } = useTracking();
  const isMounted = useIsMounted();

  const onScroll = (event: Event) => {
    const target = event.target as HTMLElement;
    if (target && target.scrollTop === 0) {
      setShowTooltip(true);
    } else {
      setShowTooltip(false);
    }
  };

  useEffect(() => {
    containerRef.current?.addEventListener('scroll', onScroll);
    return () => containerRef.current?.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (show && showTooltip && isMounted) {
      trackEvent({
        subject: 'set_fair_value_tooltip',
        action: 'view',
      });
    }
  }, [isMounted]);

  return (
    <Tooltip
      show={show && showTooltip}
      content={
        <div className="relative grid max-w-64 grid-cols-[2fr_1fr] items-center justify-around px-4 py-2">
          <p className="m-0 text-left text-xs text-p-white-100">
            Set as fair value to get updates and alerts.
          </p>
          <SetFairValueSolid className="size-16" />
          <div
            className="absolute right-0.5 top-0.5 cursor-pointer p-1"
            onClick={onClick}
          >
            <CrossSolid className="size-5 fill-p-white-100" />
          </div>
        </div>
      }
    >
      {children}
    </Tooltip>
  );
};
